import { useState } from 'react'
import type { Project } from '@shared/types'
import { Badge, Chip, TIER_STYLE, fmtFte2 } from './GccSourcingModel'
import { GCC_BADGE } from './gccExecutiveStyle'
import type { GccBadgeStyle } from './gccExecutiveStyle'
import { GccTooltip, TipEnum } from './GccTooltip'
import { GCC_TOOLTIPS } from './gcc-tooltips'
import { TIER_META, SEGMENT_META, RTAG_META } from './gcc-meta'

export function fmtUSD(usd: number): string {
  if (Math.abs(usd) >= 1_000_000) return `$${(usd / 1_000_000).toFixed(1)}M`
  if (Math.abs(usd) >= 1_000) return `$${Math.round(usd / 1_000)}K`
  return `$${Math.round(usd)}`
}

// Badge colours as in the exported GCC dashboard (WI 2487)
const SEGMENT_STYLE: Record<string, GccBadgeStyle> = { 'GCC-eligible': GCC_BADGE.green, Retained: GCC_BADGE.blue }
const ROLE_TAG_STYLE: Record<string, GccBadgeStyle> = {
  Retained: GCC_BADGE.blue, Evolved: GCC_BADGE.yellow, Consolidated: GCC_BADGE.orange, New: GCC_BADGE.green,
}

/** The End-State Roles tab of the GCC dashboard: a filtered, paginated list of the end-state roles. */
export default function RolesTab({ gcc }: { gcc: NonNullable<Project['gccData']> }) {
  const PAGE_SIZE = 15
  const [page, setPage] = useState(1)
  const [segmentFilter, setSegmentFilter] = useState<string | null>(null)
  const [tierFilter, setTierFilter] = useState<string | null>(null)
  const [tagFilter, setTagFilter] = useState<string | null>(null)

  const gccRoles = gcc.gccPopulation.endStateRoles.map(r => ({ ...r, _segment: 'GCC-eligible' as const }))
  const retainedRoles = gcc.retainedPopulation.endStateRoles.map(r => ({ ...r, _segment: 'Retained' as const }))
  const allRoles = [...gccRoles, ...retainedRoles]

  const segments = [...new Set(allRoles.map(r => r._segment))]
  const tiers = [...new Set(allRoles.map(r => r.sourcingTier).filter(Boolean))]
  const tags = [...new Set(allRoles.map(r => r.tag).filter(Boolean))]

  const filteredRoles = allRoles.filter(r =>
    (!segmentFilter || r._segment === segmentFilter) &&
    (!tierFilter || r.sourcingTier === tierFilter) &&
    (!tagFilter || r.tag === tagFilter)
  )

  const totalPages = Math.max(1, Math.ceil(filteredRoles.length / PAGE_SIZE))
  const pageRoles = filteredRoles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const toggleSegment = (v: string) => { setSegmentFilter(p => p === v ? null : v); setPage(1) }
  const toggleTier = (v: string) => { setTierFilter(p => p === v ? null : v); setPage(1) }
  const toggleTag = (v: string) => { setTagFilter(p => p === v ? null : v); setPage(1) }
  const clearFilters = () => { setSegmentFilter(null); setTierFilter(null); setTagFilter(null); setPage(1) }
  const hasFilter = !!segmentFilter || !!tierFilter || !!tagFilter

  if (allRoles.length === 0) return <p className="text-sm text-gray-400">No end-state role data.</p>

  return (
    <div>
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4 pb-3 border-b border-gray-100 text-xs">
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">Segment</span>
        {segments.map(s => (
          <Chip key={s} label={s} active={segmentFilter === s} style={SEGMENT_STYLE[s]} onClick={() => toggleSegment(s)} />
        ))}
        <span className="text-gray-200 mx-0.5">|</span>
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">Tier</span>
        {tiers.map(t => (
          <Chip key={t} label={t} active={tierFilter === t} style={TIER_STYLE[t]} onClick={() => toggleTier(t)} />
        ))}
        {tags.length > 0 && (
          <>
            <span className="text-gray-200 mx-0.5">|</span>
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">Tag</span>
            {tags.map(tag => (
              <Chip key={tag} label={tag} active={tagFilter === tag} style={ROLE_TAG_STYLE[tag]} onClick={() => toggleTag(tag)} />
            ))}
          </>
        )}
        {hasFilter && (
          <button onClick={clearFilters} className="gx-link ml-1">Clear</button>
        )}
        <span className="ml-auto text-gray-400 text-[11px]">
          {hasFilter ? `${filteredRoles.length} of ${allRoles.length}` : `${allRoles.length} roles`}
        </span>
      </div>
      <div className="gx-tbl-wrap">
        <table className="gx-tbl w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-400 border-b border-gray-100 uppercase tracking-wide">
              <th className="pb-3 font-medium">
                <GccTooltip content={GCC_TOOLTIPS.rolesRole}>Role</GccTooltip>
              </th>
              <th className="pb-3 font-medium">
                <GccTooltip content={<>{GCC_TOOLTIPS.rolesSegment}<TipEnum entries={SEGMENT_META} /></>}>Segment</GccTooltip>
              </th>
              <th className="pb-3 font-medium">
                <GccTooltip content={<>{GCC_TOOLTIPS.rolesTag}<TipEnum entries={RTAG_META} /></>}>Tag</GccTooltip>
              </th>
              <th className="pb-3 font-medium">
                <GccTooltip content={<>{GCC_TOOLTIPS.rolesTier}<TipEnum entries={TIER_META} /></>}>Tier</GccTooltip>
              </th>
              <th className="pb-3 font-medium text-right">
                <GccTooltip content={GCC_TOOLTIPS.rolesHcToday}>HC Today</GccTooltip>
              </th>
              <th className="pb-3 font-medium text-right">
                <GccTooltip content={GCC_TOOLTIPS.rolesHcEndState}>HC End-State</GccTooltip>
              </th>
              <th className="pb-3 font-medium text-right">
                <GccTooltip content={GCC_TOOLTIPS.rolesSaving}>Saving</GccTooltip>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {pageRoles.map((role, i) => {
              const saving = role.labourCostTodayUSD - role.labourCostEndStateUSD

              return (
                <tr key={i} className="hover:bg-gray-50 transition-colors">
                  <td className="py-3 font-medium text-gray-800">{role.roleName}</td>
                  <td className="py-3"><Badge label={role._segment} style={SEGMENT_STYLE[role._segment]} /></td>
                  <td className="py-3">
                    {role.tag ? <Badge label={role.tag} style={ROLE_TAG_STYLE[role.tag]} /> : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-3"><Badge label={role.sourcingTier} style={TIER_STYLE[role.sourcingTier]} /></td>
                  <td className="py-3 text-right text-gray-500">{fmtFte2(role.headcountToday)}</td>
                  <td className="py-3 text-right font-semibold gx-t-good">{fmtFte2(role.headcountEndState)}</td>
                  <td className="py-3 text-right font-semibold gx-t-good">{fmtUSD(saving)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-50">
        <span className="text-xs text-gray-400">
          Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredRoles.length)} of {filteredRoles.length}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
          >
            ← Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
            <button
              key={n}
              onClick={() => setPage(n)}
              className={`w-6 h-6 text-xs rounded-full transition-colors ${n === page ? 'font-bold gx-page-on' : 'text-gray-400 hover:bg-gray-100'}`}
            >
              {n}
            </button>
          ))}
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  )
}
